import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Chip, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { useTranslation } from 'react-i18next'

import { formatDateString } from '../utils/helpers/dates.helpers'
import { useAgamiRoutes } from '../hooks/useAgamiRoutes'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiProposalMeta } from '../utils/helpers/agami.helpers'
import { AgamiSpot } from '../types/supabase'

/**
 * The two halves of a proposal entry (05.4), shared between the cards and the
 * table so the views cannot diverge — same reason AgamiDraftChip is one
 * component. Context and actions are SEPARATE exports because the table puts
 * them in different cells, while the card footer lays them side by side.
 */

/** Who proposed it, when, and how many collapsed duplicates this entry carries (AC-1, T8). */
export const ProposalContext: FC<{ proposal: AgamiProposalMeta }> = ({ proposal }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  return (
    <span className="flex items-center gap-2 flex-wrap" data-testclass="proposal-context">
      {proposal.count > 1 && (
        <Chip
          value={`× ${proposal.count}`}
          size="sm"
          color="amber"
          variant={darkMode ? 'filled' : 'ghost'}
          className="inline-block"
          data-testclass="proposal-group-count"
        />
      )}
      <Typography variant="small" className="text-gray-600 dark:text-base-gray-500">
        {proposal.proposerName
          ? t('proposals.context', {
              name: proposal.proposerName,
              date: formatDateString(proposal.submittedAt),
            })
          : formatDateString(proposal.submittedAt)}
      </Typography>
    </span>
  )
}

/**
 * The two asymmetric actions (T2, T5): OPEN navigates to the agami — approval
 * happens with a revision, never blind — while REJECT stays in the list, because
 * it is destructive on an element one does not even want to open. Rendered only
 * when the caller holds `moderate`: absent, not disabled (AC-6).
 */
export const ProposalActions: FC<{ spot: AgamiSpot; onReject: (spot: AgamiSpot) => void }> = ({
  spot,
  onReject,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const navigate = useNavigate()
  // 05.4 wired Open to /spot/:id "until the v2 form exists": it exists now, and
  // the helper routes a flagged user to it — the form that hosts the proposal
  // banner from birth.
  const { agamiFormPath } = useAgamiRoutes()

  return (
    <span className="flex items-center gap-2">
      <Button
        size="sm"
        color={darkMode ? 'white' : ('primary' as colors)}
        variant="outlined"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          navigate(agamiFormPath(spot.id))
        }}
        data-testclass="proposal-open-btn"
      >
        {t('proposals.open')}
      </Button>
      <Button
        size="sm"
        color="red"
        variant="outlined"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onReject(spot)
        }}
        data-testclass="proposal-reject-btn"
      >
        {t('proposals.reject')}
      </Button>
    </span>
  )
}
